import { type ReactNode } from 'react';

import {
  type FormErrorMessageProps,
  type FormLabelProps,
  Input,
  InputGroup,
  InputLeftElement,
  type InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';

import { type FieldCommonProps } from '../FormField.types';

export type FieldTextProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'text' | 'email' | 'number' | 'tel';
  label?: ReactNode;
  labelProps?: FormLabelProps;
  helper?: ReactNode;
  startElement?: ReactNode;
  endElement?: ReactNode;
  formFieldErrorProps?: FormErrorMessageProps;
} & Pick<
  InputProps,
  | 'placeholder'
  | 'size'
  | 'autoFocus'
  | 'background'
  | 'borderColor'
  | 'boxShadow'
  | 'borderRadius'
> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldText = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldTextProps<TFieldValues, TName>
) => {
  const noOnWheelFn: EventListenerOrEventListenerObject = (wheelEv) => {
    wheelEv.preventDefault();
  };

  const isNumber = props.type === 'number';

  return (
    <Controller
      {...props}
      render={({ field }) => (
        <FormFieldItem>
          {!!props.label && (
            <FormFieldLabel {...props.labelProps}>{props.label}</FormFieldLabel>
          )}
          <InputGroup size={props.size}>
            <Input
              placeholder={props.placeholder}
              autoFocus={props.autoFocus}
              background={props.background}
              borderColor={props.borderColor}
              boxShadow={props.boxShadow}
              name={field.name}
              ref={field.ref}
              type={isNumber ? 'text' : props.type}
              inputMode={isNumber ? 'numeric' : undefined}
              value={field.value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isNumber) {
                  if (val === '') {
                    field.onChange(undefined);
                  } else if (/^\d*\.?\d*$/.test(val)) {
                    field.onChange(
                      val === '' ? undefined : Number(val) || undefined
                    );
                  }
                } else {
                  field.onChange(val);
                }
              }}
              onFocus={(e) => {
                if (isNumber) {
                  e.target.addEventListener('mousewheel', noOnWheelFn);
                }
              }}
              onBlur={(e) => {
                if (isNumber) {
                  e.target.removeEventListener('mousewheel', noOnWheelFn);
                }
                field.onBlur();
              }}
            />
            {!!props.startElement && (
              <InputLeftElement>{props.startElement}</InputLeftElement>
            )}
            {!!props.endElement && (
              <InputRightElement>{props.endElement}</InputRightElement>
            )}
          </InputGroup>
          {!!props.helper && <FormFieldHelper>{props.helper}</FormFieldHelper>}
          <FormFieldError {...props.formFieldErrorProps} />
        </FormFieldItem>
      )}
    />
  );
};
